import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createSubscriptionCheckout,
  createCustomerPortalSession,
  getSubscriptionStatus,
  getStripeConnectStatus,
  createStripeConnect,
  SubscriptionStatus,
  StripeConnectStatus,
} from '../api/client';
import './Upgrade.css';

interface PlanFeature {
  name: string;
  included: boolean;
  kicks?: boolean;
  soon?: boolean;
}

const plans: {
  name: string;
  key: string;
  price: string;
  period: string;
  subtitle?: string;
  highlight?: boolean;
  comingSoon?: boolean;
  features: PlanFeature[];
}[] = [
  {
    name: 'Free',
    key: 'free',
    price: '0',
    period: 'forever',
    subtitle: 'No card needed',
    features: [
      { name: 'Club subdomain', included: true },
      { name: 'Admin dashboard & content editor', included: true },
      { name: 'Team pages', included: true },
      { name: 'Player sign-up forms', included: true },
      { name: 'Contact, safeguarding & home ground', included: true },
      { name: 'Sponsors section', included: true },
      { name: 'Events calendar', included: true },
      { name: 'Photo gallery (5 albums)', included: true },
      { name: 'Club documents (20 files)', included: true },
      { name: 'Video embeds (YouTube/Vimeo)', included: true },
      { name: 'PWA — installable on mobile', included: true },
      { name: 'Club shop (5 products, 5% fee)', included: true },
      { name: 'Custom domain — add-on £2.99/mo', included: true },
      { name: 'News & match reports', included: false },
    ],
  },
  {
    name: 'Club',
    key: 'club',
    price: '8',
    period: '/month',
    subtitle: 'Everything your club needs',
    highlight: true,
    features: [
      { name: 'Everything in Free', included: true },
      { name: 'Unlimited shop products', included: true },
      { name: 'Lower platform fee (1.5%)', included: true },
      { name: 'News & match reports', included: true },
      { name: 'Photo gallery (25 albums)', included: true },
      { name: 'Club documents (100 files)', included: true },
      { name: 'Google Analytics integration', included: true },
      { name: 'Custom domain — add-on £0.99/mo', included: true },
      { name: 'Preview mode — save drafts before publishing', included: true },
      { name: '1 free Kicks AI subscription', included: true, kicks: true },
    ],
  },
  {
    name: 'Pro',
    key: 'pro',
    price: '15',
    period: '/month',
    subtitle: 'Themes and more',
    // 2026-05-19: Pro tier opened up. Theme picker now ships in admin
    // Settings → Website Theme (Pro-gated server-side).
    // 2026-06-08: Custom domain shipped as a paid add-on across all
    // tiers (£2.99/mo on Free, £0.99/mo on Club, included on Pro — the
    // authority is priceForPlan() in routes/byod-billing.ts);
    // dropped the `soon` flag.
    features: [
      { name: 'Everything in Club', included: true },
      { name: 'Choose from different themes', included: true },
      { name: 'Unlimited gallery albums & documents', included: true },
      { name: 'Remove GrassrootsFC branding', included: true },
      { name: '2 free Kicks AI subscriptions', included: true, kicks: true },
      { name: 'Priority support', included: true },
      { name: 'Custom domain — included', included: true },
      { name: 'Preview mode — save drafts before publishing', included: true },
    ],
  },
];

function Upgrade() {
  const { plan: currentPlan, clubName, clubId, refreshPlan } = useAuth();
  const kicksHref = (from: 'admin-plan-card' | 'admin-partner') =>
    `${import.meta.env.VITE_API_URL || 'https://api.grassrootsfc.co.uk'}/api/v1/go/kicks?club=${encodeURIComponent(clubId || '')}&from=${from}`;

  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [stripeConnect, setStripeConnect] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load subscription + connect status
  const loadData = async () => {
    try {
      const [subStatus, connectStatus] = await Promise.all([
        getSubscriptionStatus(),
        getStripeConnectStatus(),
      ]);
      setSubscription(subStatus);
      setStripeConnect(connectStatus);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    // Handle redirect from Stripe Checkout
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');

    if (status === 'success') {
      setSuccessMessage('Your subscription is being activated. This may take a moment...');
      // Clean up URL
      window.history.replaceState({}, '', '/upgrade');
      // Reload data after a short delay for webhook to process
      setTimeout(async () => {
        await loadData();
        await refreshPlan();
        setSuccessMessage('Your plan has been upgraded successfully!');
      }, 3000);
    } else if (status === 'cancelled') {
      setError('Checkout was cancelled. No charges were made.');
      window.history.replaceState({}, '', '/upgrade');
      setTimeout(() => setError(null), 5000);
    }
  }, []);

  const handleUpgrade = async (plan: string) => {
    setError(null);
    setIsCheckingOut(plan);
    try {
      const { url } = await createSubscriptionCheckout(plan);
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setIsCheckingOut(null);
    }
  };

  const handleManageSubscription = async () => {
    setError(null);
    try {
      const { url } = await createCustomerPortalSession();
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to open subscription management. Please try again.');
    }
  };

  const handleConnectStripe = async () => {
    setError(null);
    try {
      const { url } = await createStripeConnect();
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to start Stripe Connect setup.');
    }
  };

  const activePlan = subscription?.plan || currentPlan || 'free';
  const hasSubscription = subscription?.hasSubscription || false;
  const isPastDue = subscription?.subscriptionStatus === 'past_due';

  if (loading) {
    return (
      <div className="upgrade-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Manage Your Plan</h1>
            <p className="page-subtitle">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="upgrade-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {hasSubscription ? 'Manage Your Plan' : 'Upgrade Your Plan'}
          </h1>
          <p className="page-subtitle">
            {hasSubscription
              ? `You're on the ${activePlan.charAt(0).toUpperCase() + activePlan.slice(1)} plan`
              : `Unlock more features for ${clubName || 'your club'}`}
          </p>
        </div>
      </div>

      {/* Past Due Warning */}
      {isPastDue && (
        <div className="past-due-banner">
          <span className="past-due-icon">!</span>
          <div className="past-due-content">
            <strong>Payment failed</strong>
            <p>Your last payment didn't go through. Please update your payment method to keep your plan active.</p>
          </div>
          <button className="btn btn-primary past-due-btn" onClick={handleManageSubscription}>
            Update Payment
          </button>
        </div>
      )}

      {/* Success / Error Messages */}
      {successMessage && (
        <div className="upgrade-success">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="upgrade-error">
          {error}
        </div>
      )}

      {/* Plan Cards */}
      <div className="plans-grid">
        {plans.map((plan) => {
          const isCurrent = plan.key === activePlan;
          const isUpgrade = plan.key !== 'free' && !isCurrent && !plan.comingSoon;

          // Dynamic highlighting based on user's current plan:
          //   on free  → highlight Club (the natural upsell)
          //   on club  → highlight Pro (the next step)
          //   on pro   → highlight Pro (you're at the top — celebrate it)
          // The static `plan.highlight` flag is no longer used.
          const isHighlighted =
            (plan.key === 'club' && activePlan === 'free') ||
            (plan.key === 'pro' && (activePlan === 'club' || activePlan === 'pro'));

          // "Most Popular" only makes sense on Club when user is on Free.
          // For paid users, the badge is misleading — they've already moved past it.
          const showMostPopular = plan.key === 'club' && activePlan === 'free';

          return (
            <div
              key={plan.name}
              className={`plan-card ${isHighlighted ? 'highlighted' : ''} ${isCurrent ? 'current' : ''} ${plan.comingSoon ? 'coming-soon' : ''}`}
            >
              {plan.comingSoon && (
                <div className="plan-coming-soon-badge">Coming Soon</div>
              )}
              {showMostPopular && !isCurrent && !plan.comingSoon && (
                <div className="plan-popular">Most Popular</div>
              )}
              {isCurrent && !plan.comingSoon && (
                <div className="plan-current-badge">Current Plan</div>
              )}

              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="plan-currency">&pound;</span>
                <span className="plan-amount">{plan.price}</span>
                <span className="plan-period">{plan.period}</span>
              </div>
              {plan.subtitle && (
                <p className="plan-subtitle">{plan.subtitle}</p>
              )}

              <ul className="plan-features">
                {plan.features.map((feature) => (
                  <li
                    key={feature.name}
                    className={`plan-feature ${feature.included ? 'included' : 'excluded'} ${feature.kicks ? 'kicks-feature' : ''}`}
                  >
                    <span className="feature-icon">
                      {feature.included ? '\u2713' : '\u2717'}
                    </span>
                    {feature.kicks ? (
                      <a
                        href={kicksHref('admin-plan-card')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="feature-kicks-link"
                      >
                        <img src="/images/kicks-logo.png" alt="Kicks" className="feature-kicks-logo" />
                        <span>{feature.name}</span>
                      </a>
                    ) : (
                      <span className="feature-name">
                        {feature.name}
                        {feature.soon && <span className="feature-soon-badge">Soon</span>}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Action Button */}
              <div className="plan-action">
                {plan.comingSoon && (
                  <div className="plan-btn-placeholder plan-btn-coming-soon">Coming soon</div>
                )}
                {!plan.comingSoon && isCurrent && hasSubscription && (
                  <button
                    className="plan-btn plan-btn-manage"
                    onClick={handleManageSubscription}
                  >
                    Manage Subscription
                  </button>
                )}
                {!plan.comingSoon && isCurrent && !hasSubscription && plan.key === 'free' && (
                  <div className="plan-btn-placeholder">Current plan</div>
                )}
                {isUpgrade && !hasSubscription && (
                  <button
                    className="plan-btn plan-btn-upgrade"
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={isCheckingOut !== null}
                  >
                    {isCheckingOut === plan.key ? 'Redirecting...' : `Upgrade to ${plan.name}`}
                  </button>
                )}
                {isUpgrade && hasSubscription && (
                  <button
                    className="plan-btn plan-btn-change"
                    onClick={handleManageSubscription}
                  >
                    Change Plan
                  </button>
                )}
                {!plan.comingSoon && plan.key === 'free' && hasSubscription && !isCurrent && (
                  <div className="plan-btn-placeholder" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Kicks App Partnership */}
      <div className="kicks-partnership">
        <a href={kicksHref('admin-partner')} target="_blank" rel="noopener noreferrer" className="kicks-partnership-link">
          <img src="/images/kicks-logo.png" alt="Kicks App" className="kicks-logo" />
        </a>
        <div className="kicks-partnership-content">
          <h3 className="kicks-partnership-title">Team Management by Kicks App</h3>
          <p className="kicks-partnership-text">
            Our partnership with <a href={kicksHref('admin-partner')} target="_blank" rel="noopener noreferrer">Kicks App</a> gives
            your coaches the tools to manage teams, fixtures, and availability.
            Club plan includes 1 free Kicks AI subscription. Pro plan includes 2 free Kicks AI subscriptions.
          </p>
        </div>
      </div>

      {/* Stripe Connect Prompt — show after upgrade if not connected */}
      {hasSubscription && activePlan !== 'free' && stripeConnect && !stripeConnect.connected && (
        <div className="connect-prompt">
          <div className="connect-prompt-icon">🏪</div>
          <div className="connect-prompt-content">
            <h3 className="connect-prompt-title">Set up your shop payments</h3>
            <p className="connect-prompt-text">
              Connect your Stripe account to start accepting payments in your club shop.
              You'll be able to receive payouts directly to your bank account.
            </p>
          </div>
          <button className="btn btn-primary connect-prompt-btn" onClick={handleConnectStripe}>
            Connect Stripe
          </button>
        </div>
      )}

      {/* CTA for free plan users */}
      {!hasSubscription && activePlan === 'free' && (
        <div className="upgrade-cta">
          <div className="cta-card">
            <h3 className="cta-title">Ready to grow your club?</h3>
            <p className="cta-text">
              Upgrade for unlimited products, lower fees, news section, and more.
              Cancel any time from your subscription dashboard.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Upgrade;
